// Date formatting for the embed, split by what the value MEANS.
//
// A Postgres DATE (next_bill_date, cancel_at, service_end_date, a MID's
// opened_on, a statement month) is a calendar day with no time zone. Go
// marshals it through time.Time as "YYYY-MM-DDT00:00:00Z", so formatting it in
// the viewer's LOCAL zone reads one day early west of UTC (a Los Angeles
// customer sees Nov 14 for a Nov 15 deadline). The calendar formatters below
// therefore read and print the value in UTC. A TIMESTAMPTZ (an order's
// placed_at) is a real instant, so formatInstantDate prints it in local time.

const EM_DASH = "—";
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const RELATIVE_DAY_THRESHOLD = 6;

// A value that STARTS with a calendar day (a bare DATE, or a date-time with or
// without a zone) names that day in its own digits. This matters for an
// offset-less "2026-11-15T00:00:00": `new Date()` would read it as LOCAL time,
// which shifts a day east of UTC.
const LEADING_DATE = /^(\d{4})-(\d{2})-(\d{2})(?:$|[T ])/;

/** Epoch ms of the UTC midnight of the calendar day the value names, or null. */
function calendarDayMs(value: string | null | undefined): number | null {
  if (!value) return null;
  const m = LEADING_DATE.exec(value);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    const ms = Date.UTC(y, mo - 1, d);
    const back = new Date(ms);
    // Reject overflow such as 2026-02-31, which Date.UTC silently rolls over.
    if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) return null;
    return ms;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
}

function formatParts(ms: number, withYear: boolean, timeZone: "UTC" | undefined): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    ...(withYear ? { year: "numeric" } : {}),
    ...(timeZone ? { timeZone } : {}),
  }).format(ms);
}

/** A DATE value as "Nov 15, 2026", in UTC so every viewer sees the stored day. */
export function formatCalendarDate(value: string | null | undefined, opts?: { withYear?: boolean }): string {
  const ms = calendarDayMs(value);
  if (ms === null) return EM_DASH;
  return formatParts(ms, opts?.withYear ?? true, "UTC");
}

/**
 * A DATE value as a relative phrase when within 6 calendar days of today, else
 * absolute. Labels are sentence-start: "Today", "Tomorrow", "Yesterday",
 * "In 3 days", "3 days ago". The DATE is calendar-exact (its own digits), and
 * "today" is the VIEWER's local calendar day, so a Los Angeles customer on the
 * evening of Nov 12 sees a Nov 13 renewal as "Tomorrow". `now` is a parameter
 * so a test pins the day instead of reading the wall clock.
 */
export function formatRelativeCalendarDate(value: string | null | undefined, now: Date): string {
  const target = calendarDayMs(value);
  if (target === null) return EM_DASH;
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((target - today) / MS_PER_DAY);
  if (Math.abs(diffDays) <= RELATIVE_DAY_THRESHOLD) {
    if (diffDays === 0) return "Today";
    const phrase = new Intl.RelativeTimeFormat("en-US", { numeric: "auto" }).format(diffDays, "day");
    return phrase.charAt(0).toUpperCase() + phrase.slice(1);
  }
  return formatParts(target, true, "UTC");
}

/** A TIMESTAMPTZ value as a date in the viewer's local time zone. */
export function formatInstantDate(value: string | null | undefined, opts?: { withYear?: boolean }): string {
  if (!value) return EM_DASH;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return EM_DASH;
  return formatParts(d.getTime(), opts?.withYear ?? true, undefined);
}

/** A first-of-month DATE as "Aug 2026 statement". */
export function formatStatementMonth(value: string | null | undefined): string {
  const ms = calendarDayMs(value);
  if (ms === null) return EM_DASH;
  const month = new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric", timeZone: "UTC" }).format(ms);
  return `${month} statement`;
}
